// Тренировка дебюта: юзер повторяет главные линии, явка Lichess judges
// (миллионы партий — «что реально играют»), соперник отвечает по популярности.
// Чистая логика; сетевой fetch explorer'а живёт в services.

export interface ExplorerMove {
  uci: string;
  san: string;
  white: number;
  draws: number;
  black: number;
}

export interface ExplorerData {
  moves: ExplorerMove[];
}

export function totalGames(m: ExplorerMove): number {
  return (m.white || 0) + (m.draws || 0) + (m.black || 0);
}

/** Топ-ходы по популярности (минимум games, чтобы отсечь экзотику). */
export function topMoves(data: ExplorerData | null, limit = 5, minGames = 50): ExplorerMove[] {
  return (data?.moves || [])
    .filter((m) => totalGames(m) >= minGames)
    .sort((a, b) => totalGames(b) - totalGames(a))
    .slice(0, limit);
}

/** Процент очков за сторону side для хода. */
export function scorePctFor(m: ExplorerMove, side: 'w' | 'b'): number {
  const total = totalGames(m);
  if (!total) return 0;
  const wins = side === 'w' ? m.white : m.black;
  return Math.round(((wins + m.draws / 2) / total) * 100);
}

/** Строка дебютной аналитики: ход + явка + исходы (как explorer Lichess). */
export interface ExplorerRow {
  san: string;
  uci: string;
  games: number;
  /** Доля партий с этим ходом среди топ-ходов позиции, %. */
  popPct: number;
  whitePct: number;
  drawPct: number;
  blackPct: number;
}

/** Таблица «как ходят люди» для позиции: топ ходов с явкой и W/D/L.
 * minGames ниже, чем у тренажёра: аналитика показывает и редкие ходы. */
export function explorerRows(data: ExplorerData | null, limit = 6, minGames = 5): ExplorerRow[] {
  const top = (data?.moves || [])
    .filter((m) => totalGames(m) >= minGames)
    .sort((a, b) => totalGames(b) - totalGames(a))
    .slice(0, limit);
  const total = top.reduce((s, m) => s + totalGames(m), 0);
  return top.map((m) => {
    const games = totalGames(m);
    const pct = (n: number) => Math.round((n / games) * 100);
    return {
      san: m.san,
      uci: m.uci,
      games,
      popPct: total ? Math.round((games / total) * 100) : 0,
      whitePct: pct(m.white || 0),
      drawPct: pct(m.draws || 0),
      blackPct: pct(m.black || 0),
    };
  });
}

export interface MoveJudgement {
  ok: boolean;
  /** Место хода в топе явки (1 = самый популярный), null если вне топа. */
  rank: number | null;
  scorePct: number | null;
  /** Главные ходы для подсказки. */
  mainline: { san: string; pct: number; games: number }[];
}

/** Ход юзера ок, если он в топ-3 по популярности (теория = что реально играют). */
export function judgeUserMove(uci: string, data: ExplorerData | null, side: 'w' | 'b', topN = 3): MoveJudgement {
  const top = topMoves(data);
  const idx = top.findIndex((m) => m.uci === uci);
  const mainline = top.slice(0, topN).map((m) => ({ san: m.san, pct: scorePctFor(m, side), games: totalGames(m) }));
  if (idx >= 0 && idx < topN) {
    return { ok: true, rank: idx + 1, scorePct: scorePctFor(top[idx]!, side), mainline };
  }
  return { ok: false, rank: idx >= 0 ? idx + 1 : null, scorePct: idx >= 0 ? scorePctFor(top[idx]!, side) : null, mainline };
}

/** Ответ соперника: взвешенный сэмпл по популярности из топа. */
export function sampleReply(data: ExplorerData | null, rnd: () => number): ExplorerMove | null {
  const top = topMoves(data, 4);
  if (!top.length) return null;
  const total = top.reduce((s, m) => s + totalGames(m), 0);
  let roll = rnd() * total;
  for (const m of top) {
    roll -= totalGames(m);
    if (roll <= 0) return m;
  }
  return top[0]!;
}

const START_PLACEMENT = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR';

/** «Глубина» позиции дебюта: сколько клеток отличается от стартовой расстановки
 * (взятия + развитие). Меньше = раньше в линии. */
export function developmentScore(placement: string): number {
  const expand = (p: string): string[] => {
    const out: string[] = [];
    for (const row of p.split('/')) {
      for (const ch of row) {
        if (ch >= '1' && ch <= '8') for (let i = 0; i < +ch; i++) out.push('');
        else out.push(ch);
      }
    }
    return out;
  };
  const a = expand(START_PLACEMENT);
  const b = expand(placement);
  let diff = 0;
  for (let i = 0; i < 64; i++) if (a[i] !== b[i]) diff++;
  return diff;
}

/** Права рокировки из расстановки: даём право только если король И ладья
 * стоят на исходных клетках (консервативно — потерянное право не выдумываем,
 * но «лишнее» право возможно после манёвра туда-обратно: для тренировки дебюта
 * это приемлемое приближение). */
export function castlingFromPlacement(placement: string): string {
  const rows = placement.split('/');
  const rank1 = rows[7] || '';
  const rank8 = rows[0] || '';
  const expandRow = (row: string): string[] => {
    const out: string[] = [];
    for (const ch of row) {
      if (ch >= '1' && ch <= '8') for (let i = 0; i < +ch; i++) out.push('');
      else out.push(ch);
    }
    return out;
  };
  const r1 = expandRow(rank1);
  const r8 = expandRow(rank8);
  let rights = '';
  if (r1[4] === 'K' && r1[7] === 'R') rights += 'K';
  if (r1[4] === 'K' && r1[0] === 'R') rights += 'Q';
  if (r8[4] === 'k' && r8[7] === 'r') rights += 'k';
  if (r8[4] === 'k' && r8[0] === 'r') rights += 'q';
  return rights || '-';
}

/** Стартовая позиция дебюта по названию: обратный поиск в openings.json
 * (EPD «placement turn» → "ECO Name"). Самая ранняя позиция линии =
 * минимальное отличие от стартовой расстановки (а не «число фигур» —
 * большинство дебютных ходов ничего не бьют). */
export function findOpeningStart(openings: Record<string, string>, name: string): string | null {
  const target = name.trim().toLowerCase();
  if (!target) return null;
  let bestKey: string | null = null;
  let bestScore = Infinity;
  for (const [key, value] of Object.entries(openings)) {
    const label = value.toLowerCase();
    // "B01 Scandinavian Defense" → сравниваем часть после ECO-кода и целиком
    const namePart = label.replace(/^[a-e]\d{2}\s+/, '');
    if (namePart !== target && label !== target) continue;
    const placement = key.split(' ')[0]!;
    // взятие = строго позже (фигур меньше); при равном материале — меньше развития = раньше
    const pieces = (placement.match(/[pnbrqk]/gi) || []).length;
    const score = (32 - pieces) * 1000 + developmentScore(placement);
    if (score < bestScore) {
      bestScore = score;
      bestKey = key;
    }
  }
  if (!bestKey) return null;
  const [placement, turn] = bestKey.split(' ');
  return `${placement} ${turn || 'w'} ${castlingFromPlacement(placement!)} - 0 1`;
}
